package com.trevorwiebe.timesheet.loads.presentation.admin

import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.model.Carrier
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.core.domain.model.TimeOffRequestModel
import com.trevorwiebe.timesheet.core.domain.model.UserRole
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.loads.domain.model.isCompleted
import com.trevorwiebe.timesheet.loads.domain.model.pickups
import com.trevorwiebe.timesheet.loads.domain.model.visibleDispatchDates
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.datetime.LocalDate

data class LoadsState(
    val currentUser: DatabaseUserModel? = null,
    val userLoading: Boolean = true,
    val userLoadFailed: Boolean = false,
    val today: LocalDate,
    val loads: List<Load> = emptyList(),
    val customers: List<Customer> = emptyList(),
    val carriers: List<Carrier> = emptyList(),
    val locations: List<Location> = emptyList(),
    val trucks: List<Equipment> = emptyList(),
    val loadRates: List<LoadRate> = emptyList(),
    val suggestedLoadRates: List<LoadRate> = emptyList(),
    val users: List<DatabaseUserModel> = emptyList(),
    val approvedTimeOff: List<TimeOffRequestModel> = emptyList(),
    val filter: LoadsFilter = LoadsFilter(),
    val isLoading: Boolean = true,
    val error: String? = null,
    // Downloaded document PDFs by url, for the document viewer (admin and driver).
    val documentPdfs: Map<String, ByteArray> = emptyMap(),
    val showAddDialog: Boolean = false,
    val addLoadForm: AddLoadFormState = AddLoadFormState(),
    val isSaving: Boolean = false,
    val editingLoad: Load? = null,
    val showDeleteConfirm: Boolean = false,
    val isDeleting: Boolean = false,
    val showDuplicateDialog: Boolean = false,
    val duplicateForm: DuplicateLoadFormState = DuplicateLoadFormState(),
    val isDuplicating: Boolean = false,
) {
    val isElevated: Boolean
        get() = currentUser?.role == UserRole.ADMIN || currentUser?.role == UserRole.DISPATCHER

    val isEditing: Boolean
        get() = editingLoad != null

    val filteredLoads: List<Load>
        get() = loads.filter { load ->
            filter.customerId?.let { load.customerId == it } ?: true
        }

    val visibleDates: List<LocalDate>
        get() = visibleDispatchDates(today)

    val unscheduledLoads: List<Load>
        get() = filteredLoads
            .filter { it.scheduledDate == null }
            .sortedBy { it.originalDate }

    val isFiltered: Boolean
        get() = filter.customerId != null || filter.employeeId != null ||
            filter.pickupLocationId != null || filter.dropLocationId != null

    /**
     * The Completed screen's own "is anything narrowed" flag. Unlike [isFiltered] it counts the
     * date window, which is always applied there — without it a moved date chip hides the Clear
     * button and leaves the user no way back to the default window.
     */
    val isCompletedFiltered: Boolean
        get() {
            if (filter.employeeId != null || filter.customerId != null || filter.carrierId != null ||
                filter.pickupLocationId != null || filter.dropLocationId != null
            ) return true
            val (defaultStart, defaultEnd) = Util.lastTwoMonthsRange()
            return filter.startDate != defaultStart || filter.endDate != defaultEnd
        }

    val completedLoads: List<Load>
        get() = loads
            .filter { it.isCompleted(today) }
            .filter { load -> filter.employeeId?.let { load.employeeId == it } ?: true }
            .filter { load -> filter.customerId?.let { load.customerId == it } ?: true }
            .filter { load -> filter.carrierId?.let { load.carrierId == it } ?: true }
            .filter { load -> filter.pickupLocationId?.let { id -> load.pickups.any { it.locationId == id } } ?: true }
            .filter { load -> filter.dropLocationId?.let { id -> load.drops.any { it.locationId == id } } ?: true }
            .filter { load -> load.scheduledDate!! in filter.startDate..filter.endDate }
            .sortedByDescending { it.scheduledDate!! }

    val filterLocations: List<Location>
        get() = locations.sortedBy { it.name.lowercase() }

    val filterCustomers: List<Customer>
        get() = customers.sortedBy { it.companyName.ifBlank { it.contactName }.lowercase() }

    val filterCarriers: List<Carrier>
        get() = carriers.filter { !it.deleted }.sortedBy { it.companyName.ifBlank { it.contactName }.lowercase() }

    val completedLoadsShareText: String
        get() = CompletedLoadsShareText.build(completedLoads, locations, users)

    fun completedLoadDriverName(load: Load): String =
        users.firstOrNull { it.id == load.employeeId }?.displayName
            ?: carriers.firstOrNull { it.id == load.carrierId }
                ?.let { it.contactName.ifBlank { it.companyName } }
            ?: "—"

   val dispatchUsers: List<DatabaseUserModel>
        get() = filter.employeeId?.let { id -> users.filter { it.id == id } } ?: users

    val dispatchCarriers: List<Carrier>
        get() = carriers.filter { it.active && !it.deleted }

    fun dispatchPool(date: LocalDate): List<Load> =
        filteredLoads.filter { it.scheduledDate == date && it.employeeId.isEmpty() && it.carrierId.isEmpty() }

    fun dispatchLoadsFor(userId: String, date: LocalDate): List<Load> =
        filteredLoads.filter { it.scheduledDate == date && it.employeeId == userId }

    fun isUserOffOn(userId: String, date: LocalDate): Boolean =
        approvedTimeOff.any { it.employeeId == userId && it.requestOffTime == date }

    fun carrierLoadsFor(carrierId: String, date: LocalDate): List<Load> =
        filteredLoads.filter { it.scheduledDate == date && it.carrierId == carrierId }

    fun carrierShareText(load: Load): String = CarrierLoadShareText.build(load, locations)
}
